import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import Footer from './Footer/Footer';
import Header from './HeaderWhite/Header';
import { QueryClientProvider } from '@tanstack/react-query';
import { UserProvider } from '../Context/UserContext';
import { ListingRequestProvider } from '../Context/ListingRequestContext';
import ServiceCenter from './ServiceCenter/ServiceCenter';
import Sidebar from './Sidebar/Sidebar';
import style from './Router.module.scss?module';
import ScrollToTopBadge from '../Abstracts/ScrollToTopBadge/ScrollToTopBadge';
import { matchMultiplePaths } from '../Helper/matchMultiplePaths';
import VtsOrder from './VtsOrder/VtsOrder';
import PreHeader from './PreHeader/PreHeader';
import ListingRequestBanner from './ListingRequestBanner/ListingRequestBanner';
import ScrollToTop from '../Helper/ScrollToTop';
import NewcomerWeek from './NewcomerWeek/NewcomerWeek';
import BackorderQuantities from './Orders/BackorderQuantities/BackorderQuantities';
import { RootErrorBoundary } from '../ErrorBoundaries/RootErrorBoundary';
import { ChunkErrorBoundary } from '../ErrorBoundaries/ChunkErrorBoundary';
import { FeatureErrorBoundary } from '../ErrorBoundaries/FeatureErrorBoundary';
import { createQueryClient } from '../QueryClient/queryClient';
import { useListingRequestEnabled } from '../Hooks/useListingRequestEnabled';
import SuspenseFallback from '../Abstracts/SuspenseFallback/SuspenseFallback';

const Vts = React.lazy(() => import('./Vts/Vts'));
const Awards = React.lazy(() => import('./Awards/Awards'));

const Complaints = React.lazy(() => import('./Complaints/Complaints'));
const Contact = React.lazy(() => import('./Contact/Contact'));
const ErrorPage = React.lazy(() => import('./ErrorPage/ErrorPage'));
const Account = React.lazy(() => import('./Account/Account'));
const EmailVerification = React.lazy(
  () => import('./EmailVerification/EmailVerification')
);
const Style = React.lazy(() => import('./Style/Style'));
const LoginPage = React.lazy(() => import('./LoginPage/LoginPage'));
const Index = React.lazy(() => import('./Index/Index'));
const AboutUs = React.lazy(() => import('./AboutUs/AboutUs'));
const Faq = React.lazy(() => import('./Faq/Faq'));
const Terms = React.lazy(() => import('./Terms/Terms'));
const PrivacyPolicy = React.lazy(() => import('./PrivacyPolicy/PrivacyPolicy'));
const FdmAppPrivacy = React.lazy(
  () => import('./FdmAppPrivacy/FdmAppPrivacy')
);
const FdmAppSupport = React.lazy(
  () => import('./FdmAppSupport/FdmAppSupport')
);
const Brands = React.lazy(() => import('./Brands/Brands'));
const Products = React.lazy(() => import('./Products/Products'));
const VirtualTradeShow = React.lazy(
  () => import('./VirtualTradeShow/VirtualTradeShow')
);
const Careers = React.lazy(() => import('./Careers/Careers'));
const Basket = React.lazy(() => import('./Basket/Basket'));
const OrderList = React.lazy(() => import('./Orders/OrderList/OrderList'));
const OrderDetails = React.lazy(
  () => import('./Orders/OrderDetails/OrderDetails')
);
const TopProducts = React.lazy(() => import('./TopProducts/TopProducts'));
const UnpaidInvoiceList = React.lazy(
  () => import('./UnpaidInvoiceList/UnpaidInvoiceList')
);
const ListingSurvey = React.lazy(() => import('./ListingSurvey/ListingSurvey'));
const MyListingRequests = React.lazy(
  () => import('./MyListingRequests/MyListingRequests')
);
const OutletDeals = React.lazy(() => import('./OutletDeals/OutletDeals'));
const Bonus = React.lazy(() => import('./Bonus/Bonus'));
const CurrentSurvey = React.lazy(() => import('./Survey/Survey'));
const Newsletter = React.lazy(() => import('./Newsletter/Newsletter'));
const NewsletterConfirmation = React.lazy(
  () => import('./NewsletterConfirmation/NewsletterConfirmation')
);

function AppRouter() {
  const listingRequestEnabled = useListingRequestEnabled();
  const initialServerError = useMemo(() => {
    const raw = document.getElementById('root')?.dataset.serverError;
    return raw ? Number(raw) || 500 : null;
  }, []);
  const initialPath = useMemo(() => window.location.pathname, []);
  const [serverError, setServerError] = useState<number | null>(initialServerError);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    if (serverError && location.pathname !== initialPath) {
      setServerError(null);
    }
  }, [initialPath, location.pathname, serverError]);

  const headerHidePaths = [
    '/virtual-trade-show/*',
    '/vts',
    '/newcomer-week/home/*',
    '/newcomer-week/products/*',
    '/newcomer-week/brands/*',
    '/newcomer-week/brands/*',
    '/newcomer-week/basket',
    '/newcomer-week/favorites',
    '/newcomer-week/flavour-list',
    '/outlet',
    '/outlet/basket',
  ];
  const footerHidePaths = [
    '/virtual-trade-show/*',
    '/newcomer-week/home/*',
    '/newcomer-week/products/*',
    '/newcomer-week/brands/*',
    '/newcomer-week/brands/*',
    '/newcomer-week/basket',
    '/newcomer-week/favorites',
    '/newcomer-week/flavour-list',
    '/outlet',
    '/outlet/basket',
  ];

  const scrollToTopBadgeHidePaths = [
    '/newcomer-week/brands',
    '/newcomer-week/products',
  ];

  const { hideHeader, hideFooter, hideScrollToTopBadge } = useMemo(() => {
    const currentPath = location.pathname;

    return {
      hideHeader: matchMultiplePaths(headerHidePaths),
      hideFooter: matchMultiplePaths(footerHidePaths),
      hideScrollToTopBadge: scrollToTopBadgeHidePaths.includes(currentPath),
    };
  }, [location.pathname]);

  return (
    <>
      <ScrollToTop />
      {!hideHeader && (
        <Sidebar
          sidebarOpen={sidebarOpen}
          onSidebarToggle={(toggle) => setSidebarOpen(toggle)}
        />
      )}
      <div
        className={[
          'naskor-shop',
          style.Wrapper,
          sidebarOpen ? style.sidebarOpen : '',
        ].join(' ')}
      >
        <PreHeader />
        {!hideHeader && (
          <>
            <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            <ListingRequestBanner />
          </>
        )}
        <div
          className={'naskor-shop-main'}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Suspense fallback={<SuspenseFallback />}>
            {serverError ? (
              <ErrorPage statusCode={serverError} />
            ) : (
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/style" element={<Style />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/fdm/app/privacy" element={<FdmAppPrivacy />} />
                <Route path="/fdm/app/support" element={<FdmAppSupport />} />
                <Route path="/brands" element={<Brands />} />

                <Route path="/vts" element={<Vts />} />
                <Route
                  path="/virtual-trade-show/*"
                  element={<VirtualTradeShow />}
                />
                <Route path="/vts-order/*" element={<VtsOrder />} />

                <Route path={'/account/*'} element={<Account />} />
                <Route path={'/careers/*'} element={<Careers />} />
                <Route path="/awards" element={<Awards />} />
                <Route path="/complaints" element={<Complaints />} />
                <Route path="/contact" element={<Contact />}>
                  <Route path="/contact/supplier" element={<Contact />} />
                  <Route path="/contact/customer" element={<Contact />} />
                </Route>
                <Route
                  path="/emailVerification/:hash/:encryptedEmail?"
                  element={<EmailVerification />}
                />
                <Route path="/service-center/*" element={<ServiceCenter />} />
                <Route path="/basket" element={<Basket />} />
                <Route path="/my-orders" element={<OrderList />} />
                <Route path="/my-orders/:order_id" element={<OrderDetails />} />
                <Route
                  path="/my-orders/backorder-quantities"
                  element={<BackorderQuantities />}
                />
                <Route path="/top-products" element={<TopProducts />} />
                <Route
                  path="/my-unpaid-invoices"
                  element={<UnpaidInvoiceList />}
                />
                <Route path="/bonus" element={<Bonus />} />
                <Route path="/newcomer-week/*" element={<NewcomerWeek />} />
                <Route path="/listing-survey" element={<ListingSurvey />} />
                {listingRequestEnabled && (
                  <Route
                    path="/my-listing-requests"
                    element={<MyListingRequests />}
                  />
                )}
                <Route path="/surveys/:surveyId" element={<CurrentSurvey />} />
                <Route
                  path="/newsletterRegistration"
                  element={<Newsletter />}
                />
                <Route
                  path="/newsletterConfirmation/:hash"
                  element={<NewsletterConfirmation />}
                />
                <Route path="/outlet/*" element={<OutletDeals />} />
                <Route
                  path="/products/*"
                  element={
                    <FeatureErrorBoundary featureName="products">
                      <Products />
                    </FeatureErrorBoundary>
                  }
                />
                <Route path={'*'} element={<ErrorPage />} />
              </Routes>
            )}
          </Suspense>
        </div>
        {!hideFooter && <Footer />}
        <ScrollToTopBadge excludedRoutes={scrollToTopBadgeHidePaths} />
      </div>
    </>
  );
}

export default function Router() {
  const queryClient = useMemo(() => createQueryClient(), []);

  return (
    <RootErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ChunkErrorBoundary>
            <UserProvider>
              <ListingRequestProvider>
                <AppRouter />
              </ListingRequestProvider>
            </UserProvider>
          </ChunkErrorBoundary>
        </QueryClientProvider>
      </BrowserRouter>
    </RootErrorBoundary>
  );
}
